import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Plus, RefreshCw, UserRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useLocation } from 'wouter';
import { BranchPicker } from '../components/ui/BranchPicker';
import { Checkbox } from '../components/ui/Checkbox';
import { EmptyState } from '../components/ui/EmptyState';
import { MultiCheck, toggleInList } from '../components/ui/MultiCheck';
import { PhoneField } from '../components/ui/PhoneField';
import { SearchInput } from '../components/ui/SearchInput';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import { useAuth, type Role } from '../lib/AuthContext';
import { shiftLabel, useBranches, useRegions, useShifts, useStaffSearch, EMPLOYMENT_HINTS, EMPLOYMENT_LABELS, type EmploymentType } from '../lib/options';
import { apiErrorMessage, useToast } from '../lib/ToastContext';
import { useDebounced } from '../lib/useDebounced';
import { Loader } from '../components/ui/Spinner';

const ROLE_LABELS: Record<Role, string> = { staff: 'Staff', branch_head: 'Branch Head', region_head: 'Region Head', general_head: 'General Head', hr: 'HR', admin: 'Admin' };
const HR_ADMIN_TIER = new Set<Role>(['hr', 'admin']);

function AddStaffForm({ onDone }: { onDone: (newStaffId: string) => void }) {
  const client = useQueryClient();
  const { error: toastError } = useToast();
  const branches = useBranches();
  const regions = useRegions();

  // Basic details
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [joiningDate, setJoiningDate] = useState(new Intl.DateTimeFormat('en-CA').format(new Date()));
  const [role, setRole] = useState<Role>('staff');
  const [branchId, setBranchId] = useState('');
  const [isRoaming, setIsRoaming] = useState(false);
  const [shiftId, setShiftId] = useState('');
  const shifts = useShifts();
  const branchOptional = HR_ADMIN_TIER.has(role);

  // Branch Head / Region Head scope — separate from "Branch" above, which is where this
  // person personally works. A region head manages a set of regions (each pulling in every
  // branch under it); a branch head can be given more than one branch too.
  const [managedBranchIds, setManagedBranchIds] = useState<string[]>([]);
  const [managedRegionIds, setManagedRegionIds] = useState<string[]>([]);
  const branchesInSelectedRegions = (branches.data ?? []).filter((b) => managedRegionIds.includes(b.regionId));

  // Employment
  const [designation, setDesignation] = useState('');
  const [department, setDepartment] = useState('');

  // KYC
  const [panNumber, setPanNumber] = useState('');
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [esicNumber, setEsicNumber] = useState('');
  const [uanNumber, setUanNumber] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType>('on_roll');

  // Bank details
  const [bankAccountHolderName, setBankAccountHolderName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankIfsc, setBankIfsc] = useState('');
  const [bankName, setBankName] = useState('');

  const regionScoped = role === 'region_head' || role === 'general_head';
  const scopeIncomplete = (role === 'branch_head' && managedBranchIds.length === 0) || (regionScoped && managedRegionIds.length === 0);

  const create = useMutation({
    mutationFn: async () => (await api.post('/api/staff', {
      name, phone, email: email || undefined, joiningDate,
      roles: role === 'branch_head' ? managedBranchIds.map((id) => ({ role, branchId: id }))
        : regionScoped ? managedRegionIds.map((id) => ({ role, regionId: id }))
        : [{ role }],
      ...(branchId ? { branchAssignment: { branchId, assignedFrom: joiningDate, isRoaming, shiftId: shiftId || null } } : {}),
      designation: designation || undefined, department: department || undefined,
      panNumber: panNumber || undefined, aadhaarNumber: aadhaarNumber || undefined, esicNumber: esicNumber || undefined, uanNumber: uanNumber || undefined, employmentType,
      bankAccountHolderName: bankAccountHolderName || undefined, bankAccountNumber: bankAccountNumber || undefined, bankIfsc: bankIfsc || undefined, bankName: bankName || undefined,
    })).data as { id: string },
    onSuccess: (newStaff) => { void client.invalidateQueries({ queryKey: ['staff'] }); onDone(newStaff.id); },
    onError: (err) => toastError(apiErrorMessage(err, 'Could not add staff member')),
  });

  function submit(e: FormEvent) { e.preventDefault(); create.mutate(); }

  return (
    <form className="card mb-6" onSubmit={submit}>
      <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-muted">Basic details</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="label">Full name<input className="field mt-1" required value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="label">Phone<PhoneField className="mt-1" required value={phone} onChange={setPhone} /></label>
        <label className="label">Joining date<input type="date" className="field mt-1" required value={joiningDate} onChange={(e) => setJoiningDate(e.target.value)} /></label>
        <label className="label">Email (optional)<input type="email" className="field mt-1" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="label">Role<Select className="mt-1" value={role} onChange={(e) => setRole(e.target.value as Role)}>{(Object.keys(ROLE_LABELS) as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</Select></label>
        <label className="label">Branch (where they work){!branchOptional && ' (required)'}<BranchPicker required={!branchOptional} clearLabel={branchOptional ? 'None' : undefined} value={branchId} onChange={setBranchId} /></label>
        <label className="label">Designation (optional)<input className="field mt-1" placeholder="e.g. Delivery / Pickup Operation" value={designation} onChange={(e) => setDesignation(e.target.value)} /></label>
        <label className="label">Department (optional)<input className="field mt-1" value={department} onChange={(e) => setDepartment(e.target.value)} /></label>
      </div>
      <p className="mt-2 text-xs text-muted">Employee code is generated automatically once you save — HR can edit it afterwards.</p>

      {!branchOptional && branchId && (
        <div className="mt-4">
          <Checkbox checked={isRoaming} onChange={setIsRoaming} label="Roaming staff (can check in from any site, not just the branch above)" />
          <label className="label mt-3 sm:max-w-sm">Shift
            <Select className="mt-1" value={shiftId} onChange={(e) => setShiftId(e.target.value)}>
              <option value="">Branch default</option>
              {shifts.data?.filter((x) => x.isActive).map((x) => <option key={x.id} value={x.id}>{shiftLabel(x)}</option>)}
            </Select>
          </label>
        </div>
      )}

      {role === 'branch_head' && (
        <div className="mt-6 rounded-xl border border-border bg-canvas/50 p-4">
          <p className="label mb-2">Branches managed (select one or more)</p>
          <MultiCheck options={branches.data ?? []} selected={managedBranchIds} onToggle={(id) => toggleInList(managedBranchIds, setManagedBranchIds, id)} />
        </div>
      )}
      {regionScoped && (
        <div className="mt-6 rounded-xl border border-border bg-canvas/50 p-4">
          <p className="label mb-2">Regions managed (select one or more){role === 'general_head' && ' — a General Head sits above the Region Heads of these regions and can check in from anywhere'}</p>
          <MultiCheck options={regions.data ?? []} selected={managedRegionIds} onToggle={(id) => toggleInList(managedRegionIds, setManagedRegionIds, id)} />
          {managedRegionIds.length > 0 && (
            <div className="mt-3 rounded-xl bg-white px-3 py-2 text-sm">
              <p className="font-medium">This zone will include {branchesInSelectedRegions.length} branch{branchesInSelectedRegions.length === 1 ? '' : 'es'}:</p>
              <p className="mt-1 text-muted">{branchesInSelectedRegions.length > 0 ? branchesInSelectedRegions.map((b) => b.name).join(', ') : 'No branches in the selected regions yet.'}</p>
            </div>
          )}
        </div>
      )}

      <h2 className="mb-4 mt-6 text-sm font-bold uppercase tracking-wide text-muted">KYC details (optional for now)</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="label">PAN number<input className="field mt-1 uppercase" placeholder="ABCDE1234F" maxLength={10} value={panNumber} onChange={(e) => setPanNumber(e.target.value.toUpperCase())} /></label>
        <label className="label">Aadhaar number<input className="field mt-1" placeholder="12-digit number" maxLength={12} inputMode="numeric" value={aadhaarNumber} onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, ''))} /></label>
        <label className="label">ESIC number<input className="field mt-1" placeholder="e.g. 3713813368" value={esicNumber} onChange={(e) => setEsicNumber(e.target.value)} /></label>
        <label className="label">UAN number<input className="field mt-1" placeholder="12-digit number" maxLength={12} inputMode="numeric" value={uanNumber} onChange={(e) => setUanNumber(e.target.value.replace(/\D/g, ''))} /></label>
        <label className="label">Staff category<Select className="mt-1" value={employmentType} onChange={(e) => setEmploymentType(e.target.value as EmploymentType)}>{(Object.keys(EMPLOYMENT_LABELS) as EmploymentType[]).map((k) => <option key={k} value={k}>{EMPLOYMENT_LABELS[k]}</option>)}</Select><span className="mt-1 block text-xs text-muted">{EMPLOYMENT_HINTS[employmentType]}</span></label>
      </div>

      <h2 className="mb-4 mt-6 text-sm font-bold uppercase tracking-wide text-muted">Bank account details (optional for now)</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="label">Account holder name<input className="field mt-1" value={bankAccountHolderName} onChange={(e) => setBankAccountHolderName(e.target.value)} /></label>
        <label className="label">Bank name<input className="field mt-1" value={bankName} onChange={(e) => setBankName(e.target.value)} /></label>
        <label className="label">Account number<input className="field mt-1" inputMode="numeric" value={bankAccountNumber} onChange={(e) => setBankAccountNumber(e.target.value.replace(/\D/g, ''))} /></label>
        <label className="label">IFSC code<input className="field mt-1 uppercase" placeholder="HDFC0001234" maxLength={11} value={bankIfsc} onChange={(e) => setBankIfsc(e.target.value.toUpperCase())} /></label>
      </div>

      <div className="mt-6 flex items-center gap-2">
        <button className="btn-primary" disabled={create.isPending || scopeIncomplete}>Add staff member</button>
        <button type="button" className="btn-secondary" onClick={() => onDone('')}>Cancel</button>
      </div>
    </form>
  );
}

/** Fetched through the API client (not a plain link) so an expired 15-minute access token is
 * refreshed transparently instead of the download landing on a 401 page. */
async function downloadStaffCsv(filters: Record<string, string | undefined>) {
  const response = await api.get<Blob>('/api/staff/export', { params: filters, responseType: 'blob' });
  const filename = /filename="([^"]+)"/.exec(String(response.headers['content-disposition'] ?? ''))?.[1] ?? 'staff.csv';
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function StaffListPage() {
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const canManage = user?.roles.some((r) => r === 'hr' || r === 'admin') ?? false;
  const branches = useBranches();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q);
  const [branchId, setBranchId] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | ''>('active');

  const filters = { q: debouncedQ || undefined, branchId: branchId || undefined, role: (role as Role) || undefined, status: status || undefined };
  const staff = useStaffSearch(filters);
  const { error: toastError } = useToast();
  const exportCsv = useMutation({
    mutationFn: () => downloadStaffCsv(filters),
    onError: (err) => toastError(apiErrorMessage(err, 'Could not export staff')),
  });

  return (
    <>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div><h1 className="text-3xl font-bold">Staff</h1><p className="mt-1 text-muted">{staff.data?.length ?? 0} people</p></div>
        {canManage && (
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={exportCsv.isPending || !staff.data?.length} onClick={() => exportCsv.mutate()} title="Download name, mobile number, branch and role for the staff shown below">
              {exportCsv.isPending ? <RefreshCw size={18} className="animate-spin" /> : <Download size={18} />} Export
            </button>
            <button className="btn-primary" onClick={() => setOpen(!open)}><Plus size={18} /> New</button>
          </div>
        )}
      </div>

      {open && <AddStaffForm onDone={(id) => { setOpen(false); if (id) navigate(`/staff/${id}`); }} />}

      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <SearchInput className="sm:col-span-2" placeholder="Search by name, code, or phone" value={q} onChange={(e) => setQ(e.target.value)} />
        <BranchPicker className="mt-0" clearLabel="All branches" placeholder="Filter by branch…" value={branchId} onChange={setBranchId} />
        <Select value={role} onChange={(e) => setRole(e.target.value)}><option value="">All roles</option>{(Object.keys(ROLE_LABELS) as Role[]).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}</Select>
      </div>
      <div className="mb-4 flex gap-2">
        {(['active', 'inactive', ''] as const).map((s) => (
          <button key={s} className={`rounded-full px-3 py-1.5 text-sm font-semibold ${status === s ? 'bg-brand-600 text-white' : 'bg-white text-muted border border-border'}`} onClick={() => setStatus(s)}>
            {s === '' ? 'All' : s === 'active' ? 'Active' : 'Inactive'}
          </button>
        ))}
      </div>

      {staff.isLoading && <Loader />}
      {!staff.isLoading && staff.data?.length === 0 && <EmptyState title="No staff match these filters" hint="Try clearing the search or filters." />}

      {/* Mobile: cards */}
      <div className="space-y-3 lg:hidden">
        {staff.data?.map((s) => (
          <Link key={s.id} href={`/staff/${s.id}`} className="card flex items-center gap-3 transition hover:border-brand-500">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-100 text-brand-700"><UserRound size={20} /></div>
            <div className="min-w-0 flex-1">
              <strong className={s.isActive ? '' : 'text-muted line-through'}>{s.name}</strong>
              <p className="mt-0.5 truncate text-sm text-muted">{s.employeeCode} • {s.branchName ?? 'No branch'}{s.isRoaming ? ' (Roaming)' : ''}</p>
            </div>
            {!s.isActive && <span className="badge shrink-0 bg-red-50 text-red-700">Inactive</span>}
          </Link>
        ))}
      </div>

      {/* Desktop: table */}
      {(staff.data?.length ?? 0) > 0 && (
        <div className="hidden table-wrap lg:block">
          <table className="table">
            <thead><tr><th>Name</th><th>Employee code</th><th>Branch</th><th>Roles</th><th>Status</th></tr></thead>
            <tbody>
              {staff.data?.map((s) => (
                <tr key={s.id} className="cursor-pointer"><td colSpan={5} className="p-0">
                  <Link href={`/staff/${s.id}`} className="grid grid-cols-[2fr_1fr_1.4fr_1.6fr_0.8fr] items-center gap-3 px-4 py-3 hover:bg-canvas/60">
                    <span className={`font-semibold ${s.isActive ? '' : 'text-muted line-through'}`}>{s.name}</span>
                    <span className="text-muted">{s.employeeCode}</span>
                    <span className="text-muted">{s.branchName ?? '—'}{s.isRoaming ? ' (Roaming)' : ''}</span>
                    <span className="text-muted">{s.roles.map((r) => ROLE_LABELS[r as Role]).join(', ')}</span>
                    <span>{s.isActive ? <span className="badge">Active</span> : <span className="badge bg-red-50 text-red-700">Inactive</span>}</span>
                  </Link>
                </td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
